import { Grid } from "@mui/material";
import { useChannels } from "api/channels";
import { hasAnyAccessToResource } from "api/general";
import { DateTimeInput } from "components/FormControls/DateTimeInput";
import { FormSection } from "components/FormControls/FormSection";
import { MultiWithModal } from "components/FormControls/MultiWithModal";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { SelectInput } from "components/FormControls/Select/Select";
import { TextInput } from "components/FormControls/TextInput";
import { useMessage } from "hooks/intl";
import { useManageInfiniteScroll } from "hooks/useManageInfiniteScroll";
import { memo } from "react";
import { TransactionBackendModel } from "types/transactions";
import { useStyles } from "./Transaction.styles";

interface Props {
  isFormDataReady: boolean;
  formError: any;
  initialValues?: TransactionBackendModel;
  editMode?: boolean;
  setFieldValue: (field: string, value: string | undefined) => void;
  isReturnTransaction: boolean;
}

export const TransactionDetails = memo(
  ({
    isFormDataReady,
    formError,
    initialValues,
    editMode,
    setFieldValue,
    isReturnTransaction,
  }: Props) => {
    const classes = useStyles();
    const { message } = useMessage();
    const { useInfiniteChannelsList } = useChannels();
    const hasAnyChannelPermission = hasAnyAccessToResource("CHANNEL");

    const {
      data: infiniteChannelsList,
      size: channelsSize,
      setSize: setChannelsSize,
      isValidating,
    } = useInfiniteChannelsList();

    const {
      infiniteData: channelsList,
      anyElementsToFetch: anyChannelsToFetch,
    } = useManageInfiniteScroll(infiniteChannelsList || []);

    return (
      <FormSection
        isLoading={!isFormDataReady}
        sectionError={formError}
        title={message({ id: "transactionDetails" })}
      >
        <Grid container spacing={10}>
          <Grid xs={12} md={4} lg={3} item>
            <TextInput
              className={classes.fullWidth}
              label={
                <RequiredFieldLabel text={message({ id: "documentNumber" })} />
              }
              name="header.documentNumber"
              data-test-id="document-number-input"
            />
          </Grid>
          <Grid xs={12} md={4} lg={3} item>
            <TextInput
              className={classes.fullWidth}
              label={message({ id: "purchasePlace" })}
              name="header.purchasePlace"
              data-test-id="purchase-place-input"
            />
          </Grid>
          <Grid xs={12} md={4} lg={3} item>
            <DateTimeInput
              width="100%"
              format="yyyy-MM-dd HH:mmXXX"
              label={
                <RequiredFieldLabel text={message({ id: "documentDate" })} />
              }
              name="header.purchasedAt"
              data-test-id="document-date-input"
            />
          </Grid>
          <Grid xs={12} md={4} lg={3} item>
            <SelectInput
              className={classes.fullWidth}
              disabled={!hasAnyChannelPermission}
              items={channelsList.map((channel) => ({
                id: channel?.channelId,
                label: channel?.name,
              }))}
              label={message({ id: "channelName" })}
              name="channelId"
              data-test-id="channel-name-select-input"
              loadMoreElements={() =>
                !isValidating && setChannelsSize(channelsSize + 1)
              }
              anyElementsToFetch={anyChannelsToFetch}
              initialValue={
                initialValues && {
                  id: initialValues?.channelId,
                  label: initialValues?.channelName || "",
                }
              }
            />
          </Grid>
          <Grid xs={12} md={4} lg={3} item>
            <MultiWithModal
              className={classes.fullWidth}
              formField="header.labels"
              label={message({ id: "customAttributes" })}
              placeholder={message({
                id: "addCustomAttribute",
              })}
              data-test-id="transaction-custom-attributes"
              maxLength={255}
              maxWidth="100%"
            />
          </Grid>
          <Grid xs={12} md={4} lg={3} item>
            <SelectInput
              name="header.documentType"
              label={message({ id: "documentType" })}
              items={[
                { id: "sell", label: message({ id: "purchase" }) },
                { id: "return", label: message({ id: "return" }) },
              ]}
              onChange={(_e, value) => {
                editMode
                  ? setFieldValue(
                      "header.linkedDocumentNumber",
                      value === "sell"
                        ? undefined
                        : initialValues?.header?.linkedDocumentNumber || "",
                    )
                  : setFieldValue(
                      "header.linkedDocumentNumber",
                      value === "sell" ? undefined : "",
                    );
              }}
            />
          </Grid>
          {isReturnTransaction && (
            <Grid xs={6} item>
              <TextInput
                name="header.linkedDocumentNumber"
                label={
                  <RequiredFieldLabel
                    text={message({ id: "relatedDocumentNumber" })}
                  />
                }
                className={classes.fullWidth}
                data-test-id="return-transaction-document"
              />
            </Grid>
          )}
        </Grid>
      </FormSection>
    );
  },
);
